import { Button, Flex, Modal } from "antd";
import { useEffect, useMemo, useRef, useState } from "react";
import { useLiveQuery } from "dexie-react-hooks";
import { store } from "./Store";
import { getDeviceType, isWeixin } from "./Device";

let deferredPrompt: any = null;
const win = global as any;

// 判断用户是否安装此应用：beforeinstallprompt,如果用户已经安装过了,那么该事件不会再次触发
// 需要卸载，然后重新打开浏览器才能再次触发
win.addEventListener("beforeinstallprompt", (e: any) => {
  // e.preventDefault();
  deferredPrompt = e;
});

// 安装完成后触发,即点击安装弹窗中的“安装”后被触发
win.addEventListener("appinstalled", () => {
  deferredPrompt = null;
});

function addToDesktop() {
  // 调用prompt()方法触发安装弹窗
  deferredPrompt?.prompt();
  deferredPrompt = null;
}

let init = false;

function dateDiffGreaterThanOneWeek(date1?: Date, date2?: Date) {
  if (!date1 || !date2) return true;
  const oneWeekInMilliseconds = 1 * 24 * 60 * 60 * 1000; // 1day
  const diffMilliseconds = Math.abs(date1.getTime() - date2.getTime());
  return diffMilliseconds >= oneWeekInMilliseconds;
}
const now = new Date();

export function Desktop() {
  const options = useLiveQuery(() => store.getOptions());

  const isInit = useMemo(() => {
    if (
      init ||
      !options ||
      // 要求使用两次后弹出
      (options.useCount || 0) < 2 ||
      !dateDiffGreaterThanOneWeek(options.lastConfirmAt, now)
    ) {
      return false;
    }
    init = true;
    return true;
  }, [options]);

  if (isWeixin || !isInit) return null;
  else return <DesktopInstaller />;
}

// 定义 DraggableDiv 组件的属性类型
interface DraggableDivProps {
  children: React.ReactNode;
  onHide?: () => void;
}

const DraggableDiv: React.FC<DraggableDivProps> = ({ children, onHide }) => {
  // 使用 useRef 来引用 div 元素
  const divRef = useRef<HTMLDivElement>(null);
  // 定义状态变量
  const [isDragging, setIsDragging] = useState(false);
  const [startY, setStartY] = useState(0);
  const [initialTop, setInitialTop] = useState(0);
  // 定义隐藏阈值
  const closeThreshold = 50;

  // 开始拖动事件处理函数，兼容鼠标和触摸事件
  const handleDragStart = (
    e: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>
  ) => {
    setIsDragging(true);
    const clientY = "touches" in e ? e.touches[0].clientY : e.clientY;
    setStartY(clientY);
    if (divRef.current) {
      setInitialTop(parseInt(getComputedStyle(divRef.current).top));
    }
  };

  // 拖动移动事件处理函数，兼容鼠标和触摸事件
  const handleDragMove = (
    e: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>
  ) => {
    if (isDragging && divRef.current) {
      const currentY = "touches" in e ? e.touches[0].clientY : e.clientY;
      const deltaY = startY - currentY;
      if (deltaY < 0) return;
      const newTop = initialTop - deltaY;
      divRef.current.style.top = newTop + "px";
    }
  };

  // 拖动结束事件处理函数，兼容鼠标和触摸事件
  const handleDragEnd = () => {
    if (isDragging && divRef.current) {
      const endY = parseInt(getComputedStyle(divRef.current).top);
      const movedDistance = initialTop - endY;
      if (movedDistance >= closeThreshold) {
        divRef.current.style.display = "none";
        // 当 div 隐藏时调用 onHide 回调函数
        if (typeof onHide === "function") {
          onHide();
        }
      } else {
        divRef.current.style.top = initialTop + "px";
      }
      setIsDragging(false);
    }
  };

  return (
    <div
      ref={divRef}
      style={{
        width: "100%",
        height: "100%",
        position: "absolute",
        backgroundColor: "rgb(51 48 61)",
        color: "rgb(255, 255, 255)",
        padding: "14px 20px",
        borderRadius: 8,
        boxSizing: "border-box",
      }}
      onMouseDown={handleDragStart}
      onTouchStart={handleDragStart}
      onMouseMove={handleDragMove}
      onTouchMove={handleDragMove}
      onMouseUp={handleDragEnd}
      onMouseLeave={handleDragEnd}
      onTouchEnd={handleDragEnd}
    >
      {children}
    </div>
  );
};

function DesktopInstaller() {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const deskClick = () => {
    setIsModalOpen(false);
    addToDesktop();
  };

  const handleClose = () => {
    setIsModalOpen(false);
    store.setOption("lastConfirmAt", now);
  };

  useEffect(() => {
    win.installDesktop = () => {
      if (deferredPrompt) {
        setIsModalOpen(true);
        return true;
      } else {
        return false;
      }
    };
    const timer = setInterval(() => {
      if (deferredPrompt) {
        clearInterval(timer);
        setTimeout(() => {
          setIsModalOpen(true);
        }, 3000);
      }
    }, 100);
  }, []);

  return (
    <Modal
      open={isModalOpen && (isWeixin || !!deferredPrompt)}
      style={{ top: getDeviceType() === "Mobile" ? 10 : 24 }}
      mask={false}
      zIndex={1300}
      footer={null}
      closable={false}
      onCancel={handleClose}
      styles={{
        content: {
          background: "transparent",
          padding: 0,
          boxShadow: "none",
        },
        body: {
          position: "relative",
          height: 68,
        },
      }}
    >
      <DraggableDiv onHide={handleClose}>
        <Flex justify="space-between">
          <img
            src={require("./logo.png")}
            alt="LOGO"
            style={{ width: 40, height: 40 }}
          ></img>
          <div style={{ lineHeight: "40px", height: 40, overflow: "hidden" }}>
            {!isWeixin
              ? "安装桌面APP版提供更便捷的体验"
              : "在浏览器打开可以安装桌面APP版"}
          </div>
          <Button
            type="text"
            size="large"
            style={{ color: "#1677ff" }}
            onClick={deskClick}
          >
            安装
          </Button>
        </Flex>
      </DraggableDiv>
    </Modal>
  );
}
